// app/frontend/components/AddressAutocompleteMap.tsx
import React, { useRef, useState, useMemo } from 'react';
import { TextInput, Select, Text } from '@mantine/core';
import { Autocomplete } from '@react-google-maps/api';
import {
  convertIanaToRails,
  createTimezoneOptionsFromRails,
  isValidRailsTimezone,
} from './EventForm/utils/timezoneHelpers';
import type { RailsTimezoneData } from './EventForm/types';

interface TimezoneOption {
  value: string;
  label: string;
}

interface Area {
  id: number;
  name: string;
  country_id: number;
}

interface City {
  id: number;
  name: string;
  area_id: number;
  area_name: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onTimezoneSelect: (timeZoneId: string) => void;
  timezoneValue: string;
  railsTimezones: RailsTimezoneData[]; // Rails timezone data with value/label format
  onCitySelect?: (cityData: CityData | null) => void;
  cityValue?: CityData | null;
  areas?: Area[];
  cities?: City[];
}

interface CityData {
  cityName: string;
  countryName: string;
  countryCode: string;
  stateName: string;
  continentName: string;
  cityFound: boolean;
  missingCityName: string | null;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onTimezoneSelect,
  timezoneValue,
  railsTimezones,
  onCitySelect,
  cityValue,
  areas,
  cities,
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [loading, setLoading] = useState(false);
  const [detectedLocation, setDetectedLocation] = useState<{
    city: string;
    area: string;
    country: string;
    continent: string;
  } | null>(null);

  // Create timezone options from Rails data - memoized to prevent unnecessary re-renders
  const timezoneOptions = useMemo(() => {
    // console.log('🛐 Creating timezone options from railsTimezones:', railsTimezones?.length);
    return createTimezoneOptionsFromRails(railsTimezones || []);
  }, [railsTimezones?.length]);

  // Validate current timezone value
  const hasValidTimezone = useMemo(() => {
    if (!railsTimezones || !Array.isArray(railsTimezones)) {
      // console.warn('🛐 hasValidTimezone: railsTimezones is not valid:', railsTimezones);
      return false;
    }
    return isValidRailsTimezone(railsTimezones, timezoneValue);
  }, [railsTimezones?.length, timezoneValue]);

  // Debug logs (can be removed in production)
  const renderRef = React.useRef(0);
  renderRef.current++;

  // console.log('🛐 AddressAutocomplete render #', renderRef.current, {
  //   timezoneValue,
  //   hasValidTimezone,
  //   optionsCount: timezoneOptions.length,
  //   railsTimezonesLength: railsTimezones?.length,
  // });

  const findAreaFromCities = (cityName: string, stateName: string) => {
    if (!cities || !cityName) {
      return {
        areaName: stateName || 'Area not found',
        cityFound: false,
      };
    }

    const foundCity = cities.find((city) => city.name.toLowerCase() === cityName.toLowerCase());

    if (foundCity) {
      return {
        areaName: foundCity.area_name,
        cityFound: true,
      };
    } else {
      return {
        areaName: stateName || 'Area not found',
        cityFound: false,
      };
    }
  };

  const extractLocationData = (place: google.maps.places.PlaceResult) => {
    const components = place.address_components || [];

    let cityName = '';
    let countryName = '';
    let countryCode = '';
    let stateName = '';
    let continentName = '';

    components.forEach((component) => {
      const types = component.types;

      if (types.includes('locality')) {
        cityName = component.long_name;
      } else if (types.includes('administrative_area_level_1') && !cityName) {
        cityName = component.long_name;
      }

      if (types.includes('administrative_area_level_1')) {
        stateName = component.long_name;
      }

      if (types.includes('country')) {
        countryName = component.long_name;
        countryCode = component.short_name;
      }
    });

    const continentMap: { [key: string]: string } = {
      US: 'North America',
      CA: 'North America',
      MX: 'North America',
      GB: 'Europe',
      FR: 'Europe',
      DE: 'Europe',
      IT: 'Europe',
      ES: 'Europe',
      AU: 'Oceania',
      NZ: 'Oceania',
      JP: 'Asia',
      CN: 'Asia',
      IN: 'Asia',
      BR: 'South America',
      AR: 'South America',
      ZA: 'Africa',
      NG: 'Africa',
      EG: 'Africa',
    };

    continentName = continentMap[countryCode] || 'Unknown';

    return { cityName, countryName, countryCode, stateName, continentName };
  };

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      // console.log('🛐 Place selected:', place?.formatted_address);

      if (place && place.formatted_address && place.geometry) {
        const address = place.formatted_address;
        onChange(address);

        const { cityName, countryName, countryCode, stateName, continentName } = extractLocationData(place);
        // console.log('🛐 Extracted location:', { cityName, countryName, countryCode, stateName, continentName });

        const { areaName, cityFound } = findAreaFromCities(cityName, stateName);

        // console.log('🛐 Area from cities:', areaName, 'City found:', cityFound);

        setDetectedLocation({
          city: cityName || 'Not detected',
          area: areaName,
          country: countryName || 'Not detected',
          continent: continentName || 'Not detected',
        });

        if (onCitySelect && cityName && countryCode) {
          onCitySelect({
            cityName,
            countryName,
            countryCode,
            stateName,
            continentName,
            cityFound,
            missingCityName: !cityFound ? cityName : null,
          });
        }

        const location = place.geometry?.location;
        if (location) {
          fetchTimezoneAndUpdateField(location.lat(), location.lng());
        }
      }
    }
  };

  const fetchTimezoneAndUpdateField = async (lat: number, lng: number) => {
    setLoading(true);
    // console.log('🛐 Fetching timezone for coordinates:', lat, lng);

    try {
      const apiKey = 'N2IE9BKU2EQR';
      const url = `https://api.timezonedb.com/v2.1/get-time-zone?key=${apiKey}&format=json&by=position&lat=${lat}&lng=${lng}`;

      const response = await fetch(url);
      const data = await response.json();

      // console.log('🛐 TimeZoneDB response:', data);

      if (data.status === 'OK' && data.zoneName) {
        // Convert IANA timezone to Rails format using our utility
        const railsTimezone = convertIanaToRails(data.zoneName);

        // console.log('🛐 Timezone conversion:', {
        //   iana: data.zoneName,
        //   rails: railsTimezone,
        //   foundInRailsData: railsTimezone ? isValidRailsTimezone(railsTimezones, railsTimezone) : false,
        // });

        if (railsTimezone && isValidRailsTimezone(railsTimezones, railsTimezone)) {
          onTimezoneSelect(railsTimezone);
          // console.log('🛐 Successfully set timezone:', railsTimezone);
        } else {
          // console.warn('🛐 No Rails timezone mapping found for IANA timezone:', data.zoneName);
          // Optionally set the IANA timezone as fallback or show a warning to user
        }
      } else {
        console.error('🛐 Error in TimeZoneDB response:', data.message || 'Unknown error');
      }
    } catch (error) {
      console.error('🛐 Error fetching timezone:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
        <TextInput
          label="Address"
          name="Address"
          placeholder="Event Address"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          labelProps={{
            style: {
              display: 'block',
              textAlign: 'left',
              marginBottom: '5px',
              color: 'white',
            },
          }}
          styles={{ label: { color: '#FFF7ED' } }}
        />
      </Autocomplete>

      <Select
        label="Timezone"
        placeholder="Select timezone"
        name="Timezone"
        data={timezoneOptions}
        value={hasValidTimezone ? timezoneValue : ''} // Only show value if it's valid
        onChange={(value) => value && onTimezoneSelect(value)}
        searchable
        labelProps={{
          style: {
            display: 'block',
            textAlign: 'left',
            marginBottom: '5px',
            color: 'white',
          },
        }}
        disabled={loading}
        styles={{ label: { color: '#FFF7ED' } }}
      />

      {/* Show warning if timezone value doesn't match any option */}
      {timezoneValue && !hasValidTimezone && (
        <Text size="xs" style={{ color: '#ff6b6b', marginTop: '4px' }}>
          Warning: "{timezoneValue}" is not a valid timezone option
        </Text>
      )}

      {detectedLocation && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#2d2d2d',
            borderRadius: '8px',
            border: '1px solid #404040',
          }}
        >
          <Text size="sm" fw={500} style={{ color: '#FFF7ED', marginBottom: '8px' }}>
            Detected Location:
          </Text>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
            <div>
              <Text size="xs" style={{ color: '#888', marginBottom: '2px' }}>
                City:
              </Text>
              <Text size="sm" style={{ color: '#FFF7ED' }}>
                {detectedLocation.city}
              </Text>
            </div>
            <div>
              <Text size="xs" style={{ color: '#888', marginBottom: '2px' }}>
                Country:
              </Text>
              <Text size="sm" style={{ color: '#FFF7ED' }}>
                {detectedLocation.country}
              </Text>
            </div>
            <div>
              <Text size="xs" style={{ color: '#888', marginBottom: '2px' }}>
                Area:
              </Text>
              <Text size="sm" style={{ color: '#FFF7ED' }}>
                {detectedLocation.area}
              </Text>
            </div>
            <div>
              <Text size="xs" style={{ color: '#888', marginBottom: '2px' }}>
                Continent:
              </Text>
              <Text size="sm" style={{ color: '#FFF7ED' }}>
                {detectedLocation.continent}
              </Text>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddressAutocomplete;
